import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { applyRedo, applyUndo } from "@/utils/activityStreamUtils";
import { hashString } from "@/utils/utils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useActivityStore from "@/store/zustand/activityStore";

export interface CalculatedDiff {
  old: string;
  new: string;
  filePath: string;
}

interface FileDiffError {
  filePath: string;
  message: string;
}

const processFileDiff = (
  file,
  activities,
  diffsLookup,
  isRedo,
  selectedActivityId,
  fileToContents
) => {
  let preChangeFileContents = fileToContents[file.file_path] || "";
  let changedFileContents = preChangeFileContents;
  let hashMatches = true;

  const activitiesToGoThrough = isRedo ? [...activities].reverse() : activities;

  for (let activity of activitiesToGoThrough) {
    // Skip irrelevant activities based on redo/undo context
    if (
      (isRedo && !activity.change_undone) ||
      (!isRedo && activity.change_undone)
    )
      continue;

    // Find the diff for the current file in the activity
    const diffForFile = diffsLookup[activity.id]?.diff?.find(
      (d) => d.file_path === file.file_path
    );

    // Determine if the current file's activity is relevant
    if (diffForFile) {
      const hashToCheck = isRedo
        ? diffForFile.before_hash
        : diffForFile.after_hash;
      hashMatches =
        !hashToCheck || hashString(preChangeFileContents) === hashToCheck;

      if (!hashMatches) break; // Hash mismatch, break early

      // Apply the diff
      changedFileContents = isRedo
        ? applyRedo(preChangeFileContents, diffForFile.diff_contents)
        : applyUndo(preChangeFileContents, diffForFile.diff_contents);

      // Check if the loop has reached the activity being analyzed
      if (activity.id === selectedActivityId) break;

      preChangeFileContents = changedFileContents; // Update for next iteration
    }

    // Check if the loop has reached the activity being analyzed
    if (activity.id === selectedActivityId) break;
  }

  // Handling for hash mismatch or no applicable changes
  if (!hashMatches) {
    return {
      error: {
        filePath: file.file_path,
        message: "Hash mismatch or missing diff data.",
      },
    };
  } else if (preChangeFileContents === changedFileContents) {
    // No changes were made to the file contents
    return {
      noChanges: true,
      diff: {
        old: isRedo ? preChangeFileContents : changedFileContents,
        new: isRedo ? changedFileContents : preChangeFileContents,
        filePath: file.file_path,
      },
    };
  } else {
    // Return diff if changes were made
    return {
      diff: {
        old: isRedo ? preChangeFileContents : changedFileContents,
        new: isRedo ? changedFileContents : preChangeFileContents,
        filePath: file.file_path,
      },
    };
  }
};

/**
 * To be used to get the diffs on demand (e.g. in a callback)
 */
export const useGetActivityDiffs = () => {
  const activityLookup = useActivityStore(
    (state) => state.activityStreamLookup
  );
  const diffsLookup = useSelector(
    (state: any) => state.supabaseStore.activityStreamDiffs
  );
  const activities = useActivityStore((state) => state.activityStreamList);

  const fileToContents = useCodebaseStore((state) => state.fileToContents);

  const [activityIdToPrevResult, setActivityIdToPrevResult] = useState({});
  const activityIdToPrevResultRef = useRef(activityIdToPrevResult);
  useEffect(() => {
    activityIdToPrevResultRef.current = activityIdToPrevResult;
  }, [activityIdToPrevResult]);

  useEffect(() => {
    // When file to contents changes, clear the activityIdToPrevResult
    setActivityIdToPrevResult({});
  }, [fileToContents]);

  return (
    selectedActivityId: string
  ): {
    calculatedDiffs: CalculatedDiff[];
    calculatedDiffsActivityId: string;
    diffErrors: FileDiffError[];
  } => {
    const selectedActivity = activityLookup[selectedActivityId];

    const hasAllFileToContents = (selectedActivity?.files as any[])?.every(
      (file) =>
        fileToContents[file.file_path] !== undefined &&
        fileToContents[file.file_path] !== null
    );

    if (!selectedActivity?.files || !hasAllFileToContents) {
      return {
        calculatedDiffs: [],
        // @ts-expect-error TS2322
        calculatedDiffsActivityId: null,
        diffErrors: [],
      };
    }

    // Activities cannot change after they are created
    if (activityIdToPrevResultRef.current?.[selectedActivityId]) {
      return activityIdToPrevResultRef.current?.[selectedActivityId];
    }

    const diffs = [];
    const diffsWithNoChanges = [];
    const errors = [];

    (selectedActivity?.files as any[]).forEach((file) => {
      const result = processFileDiff(
        file,
        activities,
        diffsLookup,
        selectedActivity.change_undone,
        selectedActivity.id,
        fileToContents
      );

      if (result.diff) {
        if (result.noChanges) {
          // @ts-expect-error TS2345
          diffsWithNoChanges.push(result.diff);
        } else {
          // @ts-expect-error TS2345
          diffs.push(result.diff);
        }
      } else if (result.error) {
        // @ts-expect-error TS2345
        errors.push(result.error);
      }
    });

    const res = {
      calculatedDiffs: diffs.length ? diffs : diffsWithNoChanges,
      calculatedDiffsActivityId: selectedActivity.id,
      diffErrors: errors,
    };
    setActivityIdToPrevResult((prev) => ({
      ...prev,
      [selectedActivityId]: res,
    }));
    return res;
  };
};

/**
 * To be used when needed in a render loop
 */
export const useActivityDiffs = (selectedActivityId: string) => {
  const setReloadFiles = useCodebaseStore((state) => state.setReloadFiles);
  const fileToContents = useCodebaseStore((state) => state.fileToContents);

  const activityLookup = useActivityStore(
    (state) => state.activityStreamLookup
  );
  const diffsLookup = useSelector(
    (state: any) => state.supabaseStore.activityStreamDiffs
  );
  const activities = useActivityStore((state) => state.activityStreamList);

  const getDiffs = useGetActivityDiffs();

  // The ID of the activity these diffs are calculated for
  const [calculatedDiffsActivityId, setCalculatedDiffsActivityId] =
    useState(null);
  const [calculatedDiffs, setCalculatedDiffs] = useState<CalculatedDiff[]>([]);
  const [diffErrors, setDiffErrors] = useState<FileDiffError[]>([]);

  const selectedActivity = useMemo(() => {
    return activityLookup[selectedActivityId];
  }, [selectedActivityId, activityLookup]);

  useEffect(() => {
    if (selectedActivity?.id && activityLookup[selectedActivity.id]?.files) {
      const filePaths = (
        activityLookup[selectedActivity.id]?.files as any[]
      ).map((file) => file.file_path);
      setReloadFiles({ files: filePaths });
    }
  }, [selectedActivity]);

  useEffect(() => {
    const { calculatedDiffs, calculatedDiffsActivityId, diffErrors } =
      getDiffs(selectedActivityId);
    setCalculatedDiffs(calculatedDiffs);
    // @ts-expect-error TS2345
    setCalculatedDiffsActivityId(calculatedDiffsActivityId);
    setDiffErrors(diffErrors);
  }, [selectedActivity, activities, diffsLookup, fileToContents]);

  // Return all possible diffs that can be computed, and any specific errors encountered.
  return {
    calculatedDiffsActivityId,
    calculatedDiffs,
    diffErrors,
    selectedActivity,
  };
};
